import { useEffect, useRef, useState } from 'react';

/**
 * Avance de la generación de análisis con IAs, DENTRO del presupuesto
 * (arriba del listado). Replica el indicador de la vista de sintéticos: aro con
 * X/Y + % dentro, título, caption (el que emita el worker o, si no hay, uno
 * rotativo de las etapas típicas) y tiles con procesados/pendientes/ETA.
 *
 * Se alimenta del job global `apu_ia:{quoteId}` (WS).
 *
 * EL NÚMERO NO SE INVENTA. Hasta el 2026-08-11 este aro rellenaba los huecos
 * entre eventos con una curva asintótica (avanzaba solo hacia el 88%). El worker
 * podía pasar minutos sin publicar nada y el aro seguía subiendo: en la
 * presentación a dirección trepó hasta ~50%, se quedó ahí arrastrándose y después
 * saltó al 100% de golpe. El dueño de la empresa creyó que el sistema se había
 * colgado, cuando en realidad estaba trabajando.
 *
 * Ahora el % viene SIEMPRE del worker (que reporta por concepto y, dentro de cada
 * concepto, por insumo mientras busca precios) y solo se le exige que no vaya
 * para atrás. Cuando el trabajo es largo y ningún contador se mueve, el worker
 * manda un latido cada ~5 s y acá se dice con letras "sigue trabajando" más el
 * reloj — nunca moviendo el número.
 */

// Etapas típicas de cada análisis (para el caption cuando el worker todavía no
// mandó un mensaje propio). Lenguaje llano.
const TIPS = [
  'Armando insumos, cuadrillas y rendimientos…',
  'Buscando precios en tus catálogos de referencia…',
  'Aplicando los factores del contexto del proyecto…',
  'Resolviendo auxiliares y sub-recetas…',
  'Calculando subtotales y precio unitario…',
];

interface Job {
  stage: string;
  pct: number;
  message?: string;
  total?: number;
  processed?: number;
  startedAt: number;
  etaMs: number | null;
}

function mmss(sec: number): string {
  return `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
}

export function ApuGenerationProgress({ job }: { job: Job }) {
  // Ticker de 1s: refresca "transcurrido" y rota el caption entre eventos del WS.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((x) => x + 1), 1000);
    return () => window.clearInterval(t);
  }, []);

  const total = job.total ?? 0;
  const processed = job.processed ?? 0;
  const enCola = job.stage === 'queued';
  const realPct = Math.min(99, Math.max(0, Math.round(job.pct ?? 0)));
  // MONÓTONO: el avance nunca va para atrás ni salta solo. Es el número del
  // worker; acá únicamente se recuerda el máximo visto (un evento tardío que
  // llegue con un valor menor no hace retroceder el aro) y se anota CUÁNDO fue la
  // última vez que cambió, para poder avisar con letras que sigue trabajando.
  const visto = useRef({ pct: realPct, at: Date.now() });
  if (realPct > visto.current.pct) visto.current = { pct: realPct, at: Date.now() };
  const pct = enCola ? 0 : visto.current.pct;
  // Segundos desde el último avance REAL. El worker late cada ~5 s, así que un
  // valor alto acá significa "un paso largo en curso", no "se cortó".
  const quietoSec = Math.max(0, Math.floor((Date.now() - visto.current.at) / 1000));
  const elapsedSec = Math.max(0, Math.floor((Date.now() - job.startedAt) / 1000));
  const caption = job.message && job.message.trim()
    ? job.message
    : (enCola ? 'En cola…' : TIPS[Math.min(TIPS.length - 1, Math.floor(elapsedSec / 8))]);
  // Red por si el paso en curso es largo: TEXTO, con el reloj. Nunca un número
  // que se mueve solo. Se muestra recién pasados 10 s para no parpadear en los
  // tramos que avanzan rápido.
  const sigueTrabajando = !enCola && quietoSec >= 10;
  const etaSec = job.etaMs != null && job.etaMs > 0 ? Math.round(job.etaMs / 1000) : null;
  const pendientes = Math.max(0, total - processed);

  // Aro SVG — grande e impactante (como la vista de sintéticos)
  const SIZE = 124;
  const STROKE = 11;
  const CENTER = SIZE / 2;
  const R = CENTER - STROKE; // 51
  const C = 2 * Math.PI * R; // circunferencia
  const dash = (pct / 100) * C;

  const tile = (label: string, value: string, color?: string) => (
    <div style={{ background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '8px 12px', minWidth: 96 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: color ?? 'var(--text-primary)', marginTop: 2 }}>{value}</div>
    </div>
  );

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 24, padding: '22px 24px',
      marginBottom: 'var(--space-s)', background: 'var(--privado-weak)',
      border: '1px solid var(--privado)', borderLeft: '4px solid var(--privado)',
      borderRadius: 8, flexWrap: 'wrap',
    }}>
      {/* Aro */}
      <div style={{ position: 'relative', width: SIZE, height: SIZE, flexShrink: 0 }}>
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={CENTER} cy={CENTER} r={R} fill="none" stroke="var(--border-subtle)" strokeWidth={STROKE} />
          <circle
            cx={CENTER} cy={CENTER} r={R} fill="none" stroke="var(--privado)" strokeWidth={STROKE} strokeLinecap="round"
            strokeDasharray={`${dash} ${C}`}
            style={{ transition: 'stroke-dasharray 0.4s ease' }}
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1, color: 'var(--privado)', fontVariantNumeric: 'tabular-nums' }}>{pct}%</div>
          <div style={{ fontSize: 13, marginTop: 5, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{processed} / {total}</div>
        </div>
      </div>

      {/* Texto + tiles */}
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--privado)' }}>
          {enCola ? 'En cola…' : `Creando ${total} análisis con inteligencia artificial…`}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3 }}>{caption}</div>
        {/* Señal de vida en LETRAS para los pasos largos: el aro no se mueve
            porque no hay nada real que mover, pero el trabajo sigue. */}
        {sigueTrabajando && (
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              aria-hidden
              style={{
                width: 6, height: 6, borderRadius: '50%', background: 'var(--privado)',
                animation: 'apuLatido 1.4s ease-in-out infinite', flexShrink: 0,
              }}
            />
            {/* Sin alarmar: buscar el precio de un insumo que no está en el catálogo
                tarda, y eso es normal. Lo único que hace falta es que se vea que
                el trabajo sigue. */}
            <span>Sigue trabajando en este paso ({mmss(quietoSec)}). Los pasos largos pueden tardar varios minutos.</span>
            <style>{'@keyframes apuLatido{0%,100%{opacity:.25}50%{opacity:1}}'}</style>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {tile('Listos', String(processed), 'var(--ok)')}
          {tile('Pendientes', String(pendientes))}
          {tile('Transcurrido', mmss(elapsedSec))}
          {etaSec != null && tile('Faltan ~', mmss(etaSec))}
        </div>
      </div>
    </div>
  );
}
